import { Check, Share2 } from 'lucide-react';
import type React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import ContactButton from '@/components/shared/ContactButton';
import { Button } from '@/components/ui/button';
import { useHideHeader } from '@/hooks/useHideHeader';
import { useProgressStep } from '@/hooks/useProgressStep';
import { cn } from '@/lib/utils';

// ============================================================================
// Types & Constants
// ============================================================================

type StepStatus = 'completed' | 'current' | 'upcoming';

interface StepConfig {
	readonly number: number;
	readonly labelKey: string;
}

interface Step extends StepConfig {
	status: StepStatus;
}

const STEP_CONFIG: readonly StepConfig[] = [
	{ number: 1, labelKey: 'header.progress.gegevens' },
	{ number: 2, labelKey: 'header.progress.verzuimverzekering' },
	{ number: 3, labelKey: 'header.progress.aanvullingen' },
	{ number: 4, labelKey: 'header.progress.aanvraag' },
	{ number: 5, labelKey: 'header.progress.afronden' }
] as const;

const ROUTE_CONFIG = {
	quote: { pattern: '/aanbod', step: 2 },
	addOns: { patterns: ['/aanvulling', '/overzicht'], step: 3 },
	application: { pattern: '/aanvraag', defaultStep: 4, finalStep: { appStep: 5, progressStep: 5 } }
} as const;

const APPLICATION_STEP_REGEX = /\/aanvraag\/[^/]+\/[^/]+\/(\d+)/;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Extracts the application step number from a pathname.
 * @param pathname - The current route pathname
 * @returns The application step number (1-7) or null if not found
 */
const extractApplicationStep = (pathname: string): number | null => {
	const match = pathname.match(APPLICATION_STEP_REGEX);
	return match ? Number.parseInt(match[1], 10) : null;
};

/**
 * Determines the current progress step based on the route pathname.
 * @param pathname - The current route pathname
 * @returns The current progress step (1-5) or null if indicator should be hidden
 */
const getCurrentProgressStep = (pathname: string): number | null => {
	const { quote, addOns, application } = ROUTE_CONFIG;

	if (pathname.startsWith(quote.pattern)) {
		return quote.step;
	}

	if (addOns.patterns.some((pattern) => pathname.startsWith(pattern))) {
		return addOns.step;
	}

	if (pathname.startsWith(application.pattern)) {
		const appStep = extractApplicationStep(pathname);
		return appStep === application.finalStep.appStep
			? application.finalStep.progressStep
			: application.defaultStep;
	}

	return null;
};

/**
 * Derives the status of a step relative to the current step.
 * @param stepNumber - The step number to evaluate (1-4)
 * @param currentStep - The currently active step number (1-4)
 * @returns 'completed' | 'current' | 'upcoming'
 */
const deriveStepStatus = (stepNumber: number, currentStep: number): StepStatus => {
	if (stepNumber < currentStep) return 'completed';
	if (stepNumber === currentStep) return 'current';
	return 'upcoming';
};

// ============================================================================
// Custom Hook
// ============================================================================

interface UseProgressStepsResult {
	steps: Step[];
	currentStep: number | null;
	totalSteps: number;
	isVisible: boolean;
}

/**
 * Hook that manages progress step state based on the current route.
 * @returns Object with steps array, currentStep, totalSteps, and isVisible flag
 */
const useProgressSteps = (): UseProgressStepsResult => {
	const location = useLocation();
	const { stepOverride } = useProgressStep();

	const currentStep = useMemo(
		() => stepOverride ?? getCurrentProgressStep(location.pathname),
		[location.pathname, stepOverride]
	);

	const steps = useMemo<Step[]>(
		() =>
			currentStep === null
				? []
				: STEP_CONFIG.map((config) => ({
						...config,
						status: deriveStepStatus(config.number, currentStep)
					})),
		[currentStep]
	);

	return {
		steps,
		currentStep,
		totalSteps: STEP_CONFIG.length,
		isVisible: currentStep !== null
	};
};

// ============================================================================
// Sub-Components
// ============================================================================

interface StepIndicatorProps {
	step: Step;
}

/** Renders the circular indicator for a step (checkmark if completed, number otherwise). */
const StepIndicator = ({ step }: StepIndicatorProps): React.JSX.Element => {
	const isActive = step.status !== 'upcoming';
	const isCompleted = step.status === 'completed';

	return (
		<div
			className={cn(
				'flex items-center justify-center w-7 h-7 rounded-full border-2 border-transparent text-sm font-semibold',
				isActive && 'bg-primary text-primary-foreground',
				!isActive && 'bg-muted text-muted-foreground'
			)}
		>
			{isCompleted ? (
				<Check className="w-4 h-4" strokeWidth={3} aria-hidden="true" />
			) : (
				<span>{step.number}</span>
			)}
		</div>
	);
};

interface StepLabelProps {
	step: Step;
	label: string;
}

/** Renders the text label for a step (hidden on small screens). */
const StepLabel = ({ step, label }: StepLabelProps): React.JSX.Element => {
	const isActive = step.status !== 'upcoming';

	return (
		<span
			className={cn(
				'text-base font-medium hidden sm:inline transition-colors duration-200',
				isActive ? 'text-foreground' : 'text-muted-foreground'
			)}
		>
			{label}
		</span>
	);
};

interface ConnectorProps {
	isCompleted: boolean;
}

/** Renders the connecting line between steps (primary if completed, muted otherwise). */
const Connector = ({ isCompleted }: ConnectorProps): React.JSX.Element => (
	<div
		className={cn('w-6 sm:w-8 h-0.5 mx-2', isCompleted ? 'bg-primary' : 'bg-muted')}
		data-testid="progress-connector"
		data-completed={isCompleted}
	/>
);

interface StepItemProps {
	step: Step;
	label: string;
	isLast: boolean;
	totalSteps: number;
}

/** Renders a complete step item with indicator, label, and connector. */
const StepItem = ({ step, label, isLast, totalSteps }: StepItemProps): React.JSX.Element => {
	const { t } = useTranslation();

	const ariaLabel = useMemo(() => {
		const statusText =
			step.status === 'completed'
				? t('header.progress.statusCompleted', 'completed')
				: step.status === 'current'
					? t('header.progress.statusCurrent', 'current step')
					: t('header.progress.statusUpcoming', 'upcoming');

		return `${t('header.progress.step', 'Step')} ${step.number} ${t('header.progress.of', 'of')} ${totalSteps}: ${label}, ${statusText}`;
	}, [step, label, totalSteps, t]);

	return (
		<li
			className="flex items-center"
			aria-label={ariaLabel}
			aria-current={step.status === 'current' ? 'step' : undefined}
		>
			<div className="flex items-center gap-2">
				<StepIndicator step={step} />
				<StepLabel step={step} label={label} />
			</div>
			{!isLast && <Connector isCompleted={step.status === 'completed'} />}
		</li>
	);
};

// ============================================================================
// Main Component
// ============================================================================

interface ProgressIndicatorProps {
	/** Callback to open the contact modal (shown when header is hidden) */
	onContactClick?: () => void;
	/** Callback to open the share-quote modal (shown when header is hidden) */
	onShareClick?: () => void;
}

/**
 * Displays a multi-step progress indicator for quote and application flows.
 * Shows 5 steps: Gegevens, Verzuimverzekering, Aanvullingen, Aanvraag, Afronden.
 * Renders on `/aanbod/:id`, `/aanvulling/:id/:selected`,
 * `/overzicht/:id/:selected`, and `/aanvraag/:id/:selected/:step` routes.
 * When hideHeader is true, also displays the share-quote and contact buttons
 * on the right side (the start-over button is intentionally omitted in that mode).
 * @returns The progress indicator component or null if not on a relevant route
 */
const ProgressIndicator = ({
	onContactClick,
	onShareClick
}: ProgressIndicatorProps): React.JSX.Element | null => {
	const { t } = useTranslation();
	const { steps, currentStep, totalSteps, isVisible } = useProgressSteps();
	const hideHeader = useHideHeader();

	if (!isVisible) {
		return null;
	}

	return (
		<div className="hidden md:block bg-background border-b border-border/40 py-1">
			<div className="w-full xl:container xl:mx-auto px-5">
				<nav
					data-testid="progress-indicator"
					aria-label={t('header.progress.ariaLabel')}
					aria-describedby="progress-description"
					className="py-4 flex items-center"
				>
					<span id="progress-description" className="sr-only">
						{t('header.progress.description', {
							currentStep,
							totalSteps
						})}
					</span>
					<ol className="flex items-center justify-start">
						{steps.map((step, index) => (
							<StepItem
								key={step.number}
								step={step}
								label={t(step.labelKey)}
								isLast={index === steps.length - 1}
								totalSteps={totalSteps}
							/>
						))}
					</ol>
					{hideHeader && (onShareClick || onContactClick) && (
						<div className="ml-auto flex items-center gap-2">
							{onShareClick && (
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={onShareClick}
									iconRight={<Share2 className="w-4 h-4" />}
									data-testid="share-quote-button"
								>
									<span className="hidden sm:inline">{t('header.shareQuote')}</span>
									<span className="sm:hidden">{t('header.shareQuoteShort')}</span>
								</Button>
							)}
							{onContactClick && <ContactButton onClick={onContactClick} />}
						</div>
					)}
				</nav>
			</div>
		</div>
	);
};

export default ProgressIndicator;
